/**
 * Paywall — shown when the trial has ended without an active subscription.
 * Signup is cardless, so this screen is where the card is taken: pick a plan
 * (monthly R59 or yearly R590 — two months free), capture the card on
 * Paystack's hosted page, and start the subscription on the way back. Billing
 * and profile endpoints stay reachable behind the gate by design, otherwise a
 * lapsed account could never reach Paystack to pay.
 *
 * Author: Maré Greyling (Thevertex) <mare.greyling@gmail.com> — https://mare-greyling.com
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api.js';
import { useAuth } from '../App.jsx';
// Vault's own prices and revenue are rand (Paystack), whatever the account's currency.
import { fmtZAR as fmtR } from '../money.js';

export default function Paywall() {
  const { me, reload, logout } = useAuth();
  // A former tester arrives here after answering the go-live overlay.
  const golive = me?.golive_response || '';
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [choice, setChoice] = useState('monthly');   // monthly | yearly
  const [plans, setPlans] = useState(null);           // prices + yearly availability from /billing/status/
  const [hasCard, setHasCard] = useState(false);      // an earlier signup may still have one on file
  const [note, setNote] = useState('');               // progress text on the return trip

  // The plan choice has to survive the round trip to Paystack, so it rides in
  // sessionStorage rather than the callback URL — Paystack appends its own
  // query string and would collide with ours.
  const rememberPlan = (v) => { try { sessionStorage.setItem('vault_paywall_plan', v); } catch { /* private mode */ } };
  const recallPlan = () => { try { return sessionStorage.getItem('vault_paywall_plan'); } catch { return null; } };

  // Pull live prices + whether yearly is switched on, so the paywall always
  // matches what Paystack will actually charge (never a hardcoded figure).
  useEffect(() => {
    api('/billing/status/')
      .then((d) => {
        setPlans(d);
        setHasCard(!!d.has_payment_method);
        if (d.billing_interval && d.yearly_available) setChoice(d.billing_interval);
      })
      .catch(() => setPlans({ price_monthly_zar: 59, price_yearly_zar: 590, yearly_available: false }));

    // Back from Paystack. Verify the capture, then start the subscription in the
    // same breath — adding the card here IS the act of subscribing, so making
    // the user tap again would strand them on a screen they already paid past.
    const ref = new URLSearchParams(window.location.search).get('reference');
    if (!ref) return;
    setBusy(true); setNote('Confirming your card…');
    api('/billing/verify-capture/', { method: 'POST', body: { reference: ref } })
      .then(async (d) => {
        window.history.replaceState({}, '', '/paywall');
        if (!d.has_payment_method) throw new Error('That card was not stored. Please try again.');
        setHasCard(true);
        setNote('Starting your subscription…');
        await api('/billing/subscribe/', { method: 'POST', body: { interval: recallPlan() || 'monthly' } });
        try { sessionStorage.removeItem('vault_paywall_plan'); } catch { /* ignore */ }
        await reload();
        nav('/app');
      })
      .catch((e) => { setError(e.data?.detail || e.message); setBusy(false); setNote(''); });
  }, []);

  const monthly = plans?.price_monthly_zar ?? 59;
  const yearly = plans?.price_yearly_zar ?? 590;
  const yearlyOn = !!plans?.yearly_available;
  const saveZar = Math.max(0, monthly * 12 - yearly);
  const priceLabel = choice === 'yearly' ? `${fmtR(yearly)}/year` : `${fmtR(monthly)}/month`;

  // One button, two paths: an account that already has a card subscribes
  // outright; a cardless one (every new signup now) goes to Paystack first and
  // finishes in the return-trip effect above.
  const subscribe = async () => {
    setBusy(true); setError('');
    const interval = yearlyOn ? choice : 'monthly';
    try {
      if (hasCard) {
        await api('/billing/subscribe/', { method: 'POST', body: { interval } });
        await reload();
        nav('/app');
        return;
      }
      rememberPlan(interval);
      const d = await api('/billing/card-setup/', {
        method: 'POST',
        body: { callback_url: `${window.location.origin}/paywall` },
      });
      if (d.has_payment_method) {           // a card landed via the webhook meanwhile
        await api('/billing/subscribe/', { method: 'POST', body: { interval } });
        await reload();
        nav('/app');
        return;
      }
      window.location.assign(d.authorization_url);   // Paystack hosted page
      return;                                        // leaving — stay busy
    } catch (e) {
      setError(e.data?.detail || e.message);
    }
    setBusy(false);
  };

  return (
    <div className="min-h-full grid place-items-center px-6">
      <div className="card max-w-md text-center !p-8">
        <img src="/logo.svg" alt="" className="w-14 h-14 mx-auto" />
        <h1 className="text-2xl font-bold mt-4">
          {golive === 'accepted' ? 'Welcome to Vault Pro'
            : golive === 'declined' ? 'Your testing access has ended' : 'Your trial has ended'}
        </h1>
        <p className="mt-3 text-slate-400">
          {golive === 'declined'
            ? 'Nothing has been charged. Your budgets are kept, so you can subscribe below whenever you want to come back.'
            : "Your budgets are safe and waiting. Choose a plan and add your card on Paystack's secure page — Vault never sees the number, and you can cancel anytime."}
        </p>
        {golive === 'declined' && (
          <a href="/account" className="block mt-3 text-sm text-vblue">Download or delete my data</a>
        )}

        {yearlyOn && (
          <div className="mt-5 grid grid-cols-2 gap-2">
            {[['monthly', fmtR(monthly), 'per month'],
              ['yearly', fmtR(yearly), 'per year']].map(([k, big, sub]) => (
              <button key={k} type="button" onClick={() => setChoice(k)}
                      className={`rounded-xl border p-3 text-left transition ${
                        choice === k ? 'border-vblue bg-vblue/10' : 'border-edge hover:border-slate-500'}`}>
                <div className="text-lg font-bold">{big}</div>
                <div className="text-xs text-slate-400">{sub}</div>
                {k === 'yearly' && saveZar > 0 && (
                  <div className="text-[11px] text-emerald-400 mt-1">Save {fmtR(saveZar)} — 2 months free</div>
                )}
              </button>
            ))}
          </div>
        )}

        <p className="mt-3 text-xs text-slate-500">
          The charge appears as "Mare Greyling" on your card statement.
        </p>
        <button className="btn-primary w-full mt-4" onClick={subscribe} disabled={busy}>
          {busy ? (note || 'Starting your subscription…')
                : hasCard ? `Subscribe — ${priceLabel}` : `Add card & subscribe — ${priceLabel}`}
        </button>
        {error && <p className="text-red-400 text-sm mt-3">{error}</p>}
        <button className="mt-4 text-sm text-slate-500 hover:text-slate-300" onClick={logout}>
          Sign out
        </button>
      </div>
    </div>
  );
}
